import React from 'react';
import { Typography, Card, Row, Col } from 'antd';

const { Title, Paragraph, Text } = Typography;

const sectionStyle = { marginBottom: 24 };
const subHeadingStyle = { fontSize: 18, fontWeight: 600, marginTop: 16, marginBottom: 8 };

const Terms = () => (
  <div style={{ padding: '40px 24px' }}>
    <Row justify="center">
      <Col xs={24} sm={20} md={18} lg={14}>
        <Card className="rounded-3xl shadow-lg" style={{ border: 'none' }}>
          <Title level={1}>Terms of Service</Title>
          <Paragraph>
            <Text type="secondary">Last updated: September 26, 2026</Text>
          </Paragraph>

          <Paragraph>
            These Terms and Conditions ("Terms") form a legally binding agreement between you
            ("User", "you") and Current Care ("we", "us", "our"), the operator of the Current Care
            mobile application ("App") and the website currentcarenpl.com. By creating an account
            or using the App, you accept these Terms and our Privacy Policy.
          </Paragraph>

          <div style={sectionStyle}>
            <Title level={3}>1. Consent Summary</Title>
            <Paragraph>
              The App asks for access to your <strong>camera, microphone, location, and photo/video
              library</strong> only for the specific purposes described in Section 4. These
              permissions are optional: each one is requested only at the moment you actually use
              the related feature, and you can deny or revoke any permission at any time from your
              device settings. We never collect location in the background and we never sell or
              share your personal data for advertising.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>2. Eligibility</Title>
            <Paragraph>
              You must be at least 18 years old (or the age of majority in Nepal) to create an
              account and use the App. By using the App you confirm that the information you
              provide is accurate and up to date.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>3. Service Description</Title>
            <Paragraph>
              The App is an online marketplace and booking platform that connects you with
              independent service providers and product sellers for home, repair, maintenance, and
              other services. Core functions include: account creation and login (phone/OTP,
              Google, Apple); browsing, searching, and booking services and products; direct chat
              with service providers and delivery personnel; live order tracking; payment via
              Khalti wallet, Cash on Delivery (COD), or manual payment with proof of payment; and
              Q&A on services and products.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>4. Permissions and Intended Use</Title>
            <Paragraph>
              Each permission is requested only when you use the related feature, through the
              standard system permission dialog, and after an in-app explanation tells you why it
              is needed.
            </Paragraph>
            <ul>
              <li>
                <strong>Camera:</strong> taking a photo as payment proof for a manual (QR/offline)
                payment; setting your profile photo; providers adding product photos; sharing
                photos and short videos in chat. Never used in the background or for any hidden
                recording.
              </li>
              <li>
                <strong>Microphone:</strong> recording voice messages in chat with service
                providers and delivery personnel. Never used for ambient listening or recording
                outside the moments you explicitly activate voice recording in a chat.
              </li>
              <li>
                <strong>Location (foreground only):</strong> finding service providers near you;
                tracking your order and the delivery person's location while the order is in
                transit; optionally sharing your live location in a chat with the provider or
                delivery personnel servicing your order. Location is transmitted only when you
                trigger sharing and can be stopped at any time. We do not access your location in
                the background and never use it for advertising.
              </li>
              <li>
                <strong>Photo/Video Library:</strong> uploading your profile photo, product
                images, and images/videos attached in chat. We never scan or upload your library
                contents beyond the specific files you choose.
              </li>
            </ul>
            <Paragraph>
              Granting or denying any permission does not prevent you from using the App; it only
              disables the features listed against that permission. You can change or revoke any
              permission at any time in your device settings.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>5. Chat and Acceptable Use</Title>
            <Paragraph>
              You may send text, image, video, voice, and location messages to service providers
              and delivery personnel. You promise that content you send belongs to you or you have
              the right to share it; that you will not send unlawful, defamatory, harassing,
              obscene, or fraudulent content; that you will not share images of another person
              without their consent; and that you will not use chat for spam, scams, or
              solicitation outside the App's purpose. We may remove content that violates these
              Terms and may suspend accounts that repeatedly violate them.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>6. Purchases and Payments</Title>
            <Paragraph>
              Payment methods available in the App: Khalti digital wallet, Cash on Delivery, and
              manual payment (bank/QR transfer with proof uploaded in the App). Khalti is an
              independent third-party processor; its own Terms of Service and Privacy Policy apply
              to payment transactions. The App does not sell digital goods, subscriptions, or
              virtual items and therefore does not use in-application billing for digital content.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>7. Third-Party Services</Title>
            <Paragraph>
              The App integrates with third-party services including Google (sign-in, Firebase
              authentication/analytics), Apple (sign-in), Khalti (payments), and Google Maps
              (displaying provider and tracking locations). Your use of those integrations is
              subject to the respective third party's terms and privacy policies.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>8. Privacy and Account Deletion</Title>
            <Paragraph>
              Your privacy is governed by our Privacy Policy. You may delete your account and
              request deletion of associated data from <strong>Settings &rarr; Delete Account</strong> in
              the App, or by using the <a href="/account-deletion">account deletion form</a> on
              this website. We action deletion requests within 30 days, except where we are
              required by law to retain certain records (for example, tax or transaction records).
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>9. Intellectual Property</Title>
            <Paragraph>
              The App, its design, trademarks, and content (excluding user-generated content)
              belong to us or our licensors. You may not copy, modify, distribute, or
              reverse-engineer the App.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>10. Limitation of Liability</Title>
            <Paragraph>
              To the maximum extent permitted by law, we are not liable for indirect or
              consequential loss arising from use of the App, from a paid service performed by an
              independent provider, or from loss of or corruption of data. Nothing in these Terms
              limits liability that cannot be limited under the laws of Nepal.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>11. Termination</Title>
            <Paragraph>
              We may suspend or terminate your account if you violate these Terms, abuse
              permissions, or use the App for fraudulent or unlawful activity. You may stop using
              the App at any time and delete your account as described in Section 8.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>12. Governing Law</Title>
            <Paragraph>
              These Terms are governed by the laws of Nepal. Disputes are subject to the exclusive
              jurisdiction of the courts in Nepal.
            </Paragraph>
          </div>

          <div style={sectionStyle}>
            <Title level={3}>13. Contact</Title>
            <Paragraph>
              For questions about these Terms, permissions, privacy, or account deletion, contact
              us at <a href="mailto:support@currentcarenpl.com">support@currentcarenpl.com</a>.
            </Paragraph>
          </div>
        </Card>
      </Col>
    </Row>
  </div>
);

export default Terms;